import { createElement, MouseEvent, useCallback } from 'react';

import { ButtonWithPopover } from '@/components/form/baseInputs/Button/ButtonWithPopover';
import { IconButton } from '@/components/form/baseInputs/Button/IconButton';
import { SelectItemGroupLabel } from '@/components/form/baseInputs/SelectInput/SelectItemGroupLabel';
import { Link01Icon } from '@/components/icons/Link01Icon';
import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import { MenuItem } from '@/components/poppers/MenuPopper/MenuItem';
import { Tooltip } from '@/components/poppers/Tooltip/Tooltip';
import { getUserFacingErrorMessages } from '@/graphql/errors';
import { useReportError } from '@/hooks/useReportError';
import { useTrackUserEvent } from '@/hooks/useTrackUserEvent';
import { getIntegrationProviderDisplayName } from '@/modules/assetProviderIntegrations/shared/constants';
import { getEntityAssetsIntegrationProvider } from '@/modules/assetProviderIntegrations/shared/utils';
import { useCanManageIntegrationData } from '@/modules/authentication/hooks/useCanManageIntegrationData';
import {
  EntityValuationAction,
  EntityValuationActionDefinition,
} from '@/modules/entities/details/EntityValuation/EntityValuationActions';
import { useResyncEntityValuationMutation } from '@/modules/entities/details/EntityValuation/graphql/EntityValuation.generated';
import { EstatePage_EntityFragment } from '@/modules/estate/graphql/EstatePageData.generated';
import { useHouseholdDetailsContext } from '@/modules/household/contexts/householdDetails.context';
import { COLORS } from '@/styles/tokens/colors';
import { diagnostics } from '@/utils/diagnostics';

import { getEstateIntegrationActions } from './getEstateIntegrationActions';
import { useUnlinkEntityIntegrationMutation } from './graphql/UnlinkEntityIntegration.generated';

export interface LinkEntityIntegrationButtonProps {
  /** The holdings group entity to manage integration for */
  entity: EstatePage_EntityFragment;
  /** Callback when user wants to link data */
  onLinkClick: () => void;
  /** Whether the popper menu should be open (controlled) */
  isPopperMenuOpen: boolean;
  /** Callback when the popper menu opens */
  onOpenPopperMenu: () => void;
  /** Callback when the popper menu closes */
  onClosePopperMenu: () => void;
  isIntegrationExcluded: boolean;
}

export function LinkEntityIntegrationButton({
  entity,
  onLinkClick,
  isPopperMenuOpen,
  onOpenPopperMenu,
  onClosePopperMenu,
  isIntegrationExcluded,
}: LinkEntityIntegrationButtonProps) {
  const { showFeedback } = useFeedback();
  const { reportError } = useReportError();
  const trackUserEvent = useTrackUserEvent();
  const { householdId } = useHouseholdDetailsContext();

  // View-only pattern support
  const canManageIntegrationData = useCanManageIntegrationData();
  const isViewOnly = !canManageIntegrationData;

  const [unlinkEntityIntegration, { loading: unlinkLoading }] =
    useUnlinkEntityIntegrationMutation({
      onCompleted: () => {
        showFeedback('Successfully unlinked entity from Addepar', {
          variant: 'success',
        });
      },
      onError: (error) => {
        diagnostics.error('Failed to unlink entity integration', error);
        reportError(
          'Failed to unlink integration. Please try again.',
          error instanceof Error
            ? error
            : new Error('Failed to unlink integration')
        );
      },
    });

  const [resyncEntityValuation, { loading: resyncLoading }] =
    useResyncEntityValuationMutation({
      variables: {
        entityId: entity.id,
      },
      onCompleted: () => {
        showFeedback('Initiated resync of valuation data', {
          variant: 'success',
        });
      },
      onError: (error) => {
        const userFacingMessages = getUserFacingErrorMessages(error);
        const isHandledError =
          !error.networkError &&
          userFacingMessages.length > 0 &&
          userFacingMessages.length === error.graphQLErrors.length;

        if (isHandledError) {
          showFeedback(
            `Failed to initiate valuation resync: ${userFacingMessages.join(' ')}`
          );
        } else {
          showFeedback(
            'Failed to initiate valuation resync. Please try again.'
          );
          reportError('Failed to initiate valuation resync', error);
        }
      },
    });

  // Action handler for integration actions
  const handleIntegrationAction = useCallback(
    (action: EntityValuationActionDefinition) => {
      switch (action.kind) {
        case EntityValuationAction.RESYNC_ENTITY_VALUATION:
          void resyncEntityValuation();
          break;
        case EntityValuationAction.VIEW_ON_INTEGRATED_PLATFORM:
          window.open(
            `/api/v1/integration/redirect/integration-entity?householdId=${householdId}&integrationEntityId=${action.integrationEntityId}`,
            '_blank'
          );
          break;
        case EntityValuationAction.MANAGE_DATA_LINK:
          onLinkClick();
          break;
        default:
          diagnostics.warn(`Unhandled integration action: ${action.kind}`);
      }
    },
    [resyncEntityValuation, householdId, onLinkClick]
  );

  // Don't render the button if entity type is excluded from integrations
  if (isIntegrationExcluded) {
    return null;
  }

  const hasIntegrationEntities = Boolean(
    entity.integrationEntities && entity.integrationEntities.length > 0
  );

  // If entity has integration entities, show integration actions dropdown
  if (hasIntegrationEntities) {
    const integrationActions = getEstateIntegrationActions(entity, isViewOnly);

    // If no integration actions available, fall back to unlink behavior
    if (integrationActions.length === 0) {
      const handleUnlink = () => {
        void unlinkEntityIntegration({
          variables: {
            entityId: entity.id,
          },
        }).catch((error: unknown) => {
          diagnostics.error(
            'Failed to execute unlinkEntityIntegration mutation',
            error instanceof Error ? error : new Error('Unknown mutation error')
          );
        });
      };

      return (
        <IconButton
          size="xs"
          variant="secondary"
          icon={Link01Icon}
          loading={unlinkLoading}
          onClick={(event: MouseEvent) => {
            event.stopPropagation();
            handleUnlink();
          }}
          ariaLabel="Unlink data"
        />
      );
    }

    const provider = getEntityAssetsIntegrationProvider(entity);
    const providerDisplayName = provider
      ? getIntegrationProviderDisplayName(provider)
      : null;

    // Group actions by type for the popover
    const linkedFromActions = integrationActions.filter(
      (action) =>
        action.kind === EntityValuationAction.VIEW_ON_INTEGRATED_PLATFORM
    );
    const otherActions = integrationActions.filter(
      (action) =>
        action.kind !== EntityValuationAction.VIEW_ON_INTEGRATED_PLATFORM
    );

    const handleActionClick = (action: EntityValuationActionDefinition) => {
      trackUserEvent('client_onboarding data_link_dropdown_clicked', {
        action:
          action.kind === EntityValuationAction.MANAGE_DATA_LINK
            ? 'manage_data_link'
            : action.kind,
        source: 'client_creation',
      });
      handleIntegrationAction(action);
    };

    return (
      <div
        onClick={(event: MouseEvent) => {
          // Stop propagation to prevent accordion toggle
          event.stopPropagation();
        }}
      >
        <ButtonWithPopover
          label="Linked"
          size="xs"
          variant="secondary"
          startIcon={Link01Icon}
          loading={resyncLoading}
          popperVariant="menuBelow"
          isOpen={isPopperMenuOpen}
          onOpen={onOpenPopperMenu}
          onClose={onClosePopperMenu}
          onClick={(event: MouseEvent) => {
            event.stopPropagation();
            // When controlled, we need to handle the click to toggle state
            if (isPopperMenuOpen) {
              onClosePopperMenu();
            } else {
              onOpenPopperMenu();
            }
          }}
        >
          {/* Other actions */}
          {otherActions.map((action, index) => {
            // otherActions only contains actions that are NOT VIEW_ON_INTEGRATED_PLATFORM, so they have 'label'
            const actionWithLabel = action as Exclude<
              EntityValuationActionDefinition,
              { kind: EntityValuationAction.VIEW_ON_INTEGRATED_PLATFORM }
            >;
            const IconComponent = actionWithLabel.icon;
            return (
              <MenuItem
                key={`other-action-${index}`}
                label={actionWithLabel.label}
                icon={<IconComponent size={16} color={COLORS.TEXT.SECONDARY} />}
                onClick={() => handleActionClick(action)}
              />
            );
          })}

          {/* Linked from {provider} section */}
          {linkedFromActions.length > 0 && (
            <>
              <SelectItemGroupLabel
                label={`Linked from ${providerDisplayName || 'integration'}`}
              />
              {linkedFromActions.map((action, index) => {
                // linkedFromActions only contains VIEW_ON_INTEGRATED_PLATFORM actions, so they have 'name'
                const actionWithName = action as Extract<
                  EntityValuationActionDefinition,
                  { kind: EntityValuationAction.VIEW_ON_INTEGRATED_PLATFORM }
                >;
                const IconComponent = actionWithName.icon;
                return (
                  <MenuItem
                    key={`linked-action-${index}`}
                    label={actionWithName.name}
                    icon={createElement(IconComponent, {
                      size: 16,
                      color: COLORS.TEXT.SECONDARY,
                    })}
                    onClick={() => handleActionClick(action)}
                  />
                );
              })}
            </>
          )}
        </ButtonWithPopover>
      </div>
    );
  }

  return (
    <Tooltip title="Link external data">
      <IconButton
        size="xs"
        variant="secondary"
        icon={Link01Icon}
        onClick={(event: MouseEvent) => {
          event.stopPropagation();
          onLinkClick();
        }}
        ariaLabel="Link data"
      />
    </Tooltip>
  );
}
